import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TurnstileWidget from '../components/TurnstileWidget';
import { useCaptchaConfig } from '../hooks/useCaptchaConfig';
import CelestialLayout from '../components/celestial/CelestialLayout';
import TopNav from '../components/celestial/TopNav';
import MaterialIcon from '../components/celestial/MaterialIcon';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const captcha = useCaptchaConfig();

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileResetKey((k) => k + 1);
  }, []);

  const handleTurnstileToken = useCallback((token: string | null) => {
    setTurnstileToken(token);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (captcha.required && (!turnstileToken || !turnstileToken.trim())) {
      setError('Lütfen güvenlik doğrulamasını tamamlayın.');
      return;
    }

    setLoading(true);
    try {
      await login(
        email,
        password,
        captcha.required ? turnstileToken ?? undefined : undefined,
      );
      navigate('/profil');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Giriş başarısız';
      setError(msg);
      if (captcha.required) resetTurnstile();
    } finally {
      setLoading(false);
    }
  }

  const submitDisabled =
    loading ||
    captcha.loading ||
    (captcha.required && (!turnstileToken || !turnstileToken.trim()));

  return (
    <CelestialLayout orbs>
      <TopNav />
      <main className="celestial-main celestial-auth">
        <div className="celestial-glass celestial-glass--strong celestial-auth__card">
          <div className="celestial-auth__inner">
            <div style={{ marginBottom: '1.5rem' }}>
              <h1 className="celestial-headline-lg">Giriş Yap</h1>
              <p className="celestial-body-lg" style={{ fontSize: '0.95rem', marginTop: '0.5rem' }}>
                Hesabınızla giriş yaparak kullanım bilgilerinizi ve abonelik durumunuzu görüntüleyin.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="celestial-error">{error}</div>}

              <div className="celestial-form-group">
                <label className="celestial-label" htmlFor="email">
                  E-posta
                </label>
                <input
                  id="email"
                  className="celestial-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="celestial-form-group">
                <label className="celestial-label" htmlFor="password">
                  Şifre
                </label>
                <input
                  id="password"
                  className="celestial-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {captcha.loading && (
                <p className="celestial-caption" style={{ textTransform: 'none', marginBottom: '1rem' }}>
                  Güvenlik doğrulaması yükleniyor…
                </p>
              )}
              {!captcha.loading && captcha.required && captcha.siteKey && (
                <div className="celestial-turnstile-wrap">
                  <TurnstileWidget
                    siteKey={captcha.siteKey}
                    onToken={handleTurnstileToken}
                    resetKey={turnstileResetKey}
                  />
                </div>
              )}

              <button
                type="submit"
                className="celestial-btn-primary"
                disabled={submitDisabled}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                {loading ? 'Giriş yapılıyor…' : 'Giriş Yap'}
                {!loading && <MaterialIcon name="arrow_forward" size={20} />}
              </button>

              <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
                <Link to="/" className="celestial-btn-ghost">
                  <MaterialIcon name="arrow_back" size={18} />
                  Ana sayfaya dön
                </Link>
              </div>
            </form>
          </div>
        </div>
      </main>
    </CelestialLayout>
  );
}
